import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ROLE_SCOPE, type Role } from "@workcore/schemas";
import { api, checkedWrite } from "../api";
import { KIND_LABEL, parsePrincipal, useContainers, useDefinitions, useDirectory, useRefresh, useRoles } from "../directory";
import { ErrorBox, PageHeader, Panel, RowMenu, SidePanel, When, when } from "../ui";
import { HistoryLink } from "../activity";
import { confirmAction } from "../confirm";

const ROLES: { role: Role; label: string; what: string }[] = [
  { role: "system_admin", label: "System admin", what: "users, groups, service accounts, which definitions and projects exist; no project content" },
  { role: "workflow_admin", label: "Workflow admin", what: "edits one workflow definition and its templates" },
  { role: "project_admin", label: "Project admin", what: "sees everything in one project and manages its access" },
  { role: "member", label: "Member", what: "works on objects they can see" },
  { role: "guest", label: "Guest", what: "reads and comments only" },
];

const GRANTABLE = ROLES.filter((r) => r.role !== "member" && r.role !== "guest");
const BASE = new Set<Role>(["member", "guest"]);

export function RolesPage() {
  const { slug = "" } = useParams();
  const roles = useRoles(slug);
  const dir = useDirectory(slug);
  const defs = useDefinitions(slug).data ?? [];
  const containers = (useContainers(slug).data ?? []).filter((c) => !c.isTemplate);
  const refresh = useRefresh(slug);
  const [principal, setPrincipal] = useState("");
  const [role, setRole] = useState<Role>("workflow_admin");
  const [scopeId, setScopeId] = useState("");
  const [adding, setAdding] = useState(false);
  const scopeKind = ROLE_SCOPE[role];
  const scopeName = (kind: string, id: number | null) =>
    id === null ? "workspace" : kind === "definition" ? `${defs.find((d) => d.id === id)?.name ?? `definition ${id}`} (workflow)` : `${containers.find((c) => c.id === id)?.name ?? `project ${id}`} (project)`;

  const grant = useMutation({
    mutationFn: () => checkedWrite("POST", `/v1/ws/${slug}/roles`, { principal: parsePrincipal(principal), role, ...(scopeKind === "workspace" ? {} : { scopeId: Number(scopeId) }) }),
    onSuccess: () => (setPrincipal(""), setScopeId(""), setAdding(false), refresh()),
  });
  const revoke = useMutation({ mutationFn: (id: number) => api("DELETE", `/v1/ws/${slug}/roles/${id}`), onSuccess: refresh });

  return (
    <>
      <PageHeader title="Roles" description="Who administers what. Everyone also has one base role, member or guest, set when they are invited." actions={<><HistoryLink slug={slug} kind="roles" /><button onClick={() => setAdding(true)}>Grant a role</button></>} />
      <SidePanel title="Grant a role" open={adding} onClose={() => setAdding(false)} onSubmit={() => grant.mutate()} submitLabel="Grant" busy={grant.isPending || !principal} error={grant.error}>
        <div className="sentence">
          <span>Grant</span>
          <select aria-label="Who" required value={principal} autoFocus onChange={(e) => setPrincipal(e.target.value)}>
            <option value="">someone…</option>
            {dir.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span>the</span>
          <select aria-label="Role" value={role} onChange={(e) => (setRole(e.target.value as Role), setScopeId(""))}>
            {/* member/guest is each user's single base role, set by invitation, not granted here */}
            {GRANTABLE.map((r) => <option key={r.role} value={r.role}>{r.label}</option>)}
          </select>
          <span>role{scopeKind === "workspace" ? " for the whole workspace." : " on"}</span>
          {scopeKind === "definition" && (
            <select aria-label="Workflow" required value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
              <option value="">which workflow…</option>
              {defs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          {scopeKind === "container" && (
            <select aria-label="Project" required value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
              <option value="">which project…</option>
              {containers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
        <p className="muted small">{ROLES.find((r) => r.role === role)?.label}: {ROLES.find((r) => r.role === role)?.what}.</p>
        {scopeKind === "container" && containers.length === 0 && <p className="muted small">No projects yet.</p>}
      </SidePanel>
      <Panel title="Assignments" plain>
        <ErrorBox error={roles.error ?? revoke.error} />
        <table className="grid">
          <thead><tr><th>Who</th><th>Role</th><th>Scope</th><th>Since</th><th /></tr></thead>
          <tbody>
            {roles.data?.map((r) => (
              <tr key={r.id}>
                <td>{dir.label(r.principal)} <span className="muted small">{KIND_LABEL[r.principal.kind]}</span></td>
                <td><span className="badge">{r.role.replace("_", " ")}</span></td>
                <td>{scopeName(r.scopeKind, r.scopeId)}</td>
                <td><When at={r.createdAt} /></td>
                <td className="row-actions">
                  {BASE.has(r.role) ? (
                    <span className="lock" title="Each person has exactly one base role, member or guest. Change it on the Users page.">Base role</span>
                  ) : (
                    <RowMenu label={`Actions for ${dir.label(r.principal)} as ${r.role.replace("_", " ")}`} items={[{ label: "Revoke…", danger: true, onSelect: () => void confirmAction({ title: `Revoke ${r.role.replace("_", " ")} from ${dir.label(r.principal)}?`, body: "It stops applying on their next request. You can grant it again.", action: "Revoke", check: () => api("DELETE", `/v1/ws/${slug}/roles/${r.id}`, { dryRun: true }) }).then((ok) => ok && revoke.mutate(r.id)) }]} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </>
  );
}
